import React, { useEffect, useState } from 'react';
import { MeterReadingForm } from './components/MeterReadingForm';
import { InvoiceGenerator } from './components/InvoiceGenerator';
import { History } from './components/History';
import { Settings } from './components/Settings';
import { HomeIcon, FileTextIcon, HistoryIcon, SettingsIcon } from 'lucide-react';
// Define types
export type Shop = {
  id: number;
  name: string;
  currentReading: number;
  previousReading: number;
  additionalCharges: number;
};
export type ReadingPeriod = {
  month: number; // 0-11 (Jan-Dec)
  year: number;
};
export type HistoryEntry = {
  period: ReadingPeriod;
  shops: Shop[];
};
export type AppSettings = {
  costPerUnit: number;
};
export function App() {
  // Navigation state
  const [activeTab, setActiveTab] = useState<'readings' | 'invoices' | 'history' | 'settings'>('readings');
  // App data
  const [shops, setShops] = useState<Shop[]>([]);
  const [settings, setSettings] = useState<AppSettings>({
    costPerUnit: 5
  });
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  // Current period for readings
  const currentDate = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState<ReadingPeriod>({
    month: currentDate.getMonth(),
    year: currentDate.getFullYear()
  });
  // Initialize shops on first load
  useEffect(() => {
    // Try to load from localStorage first
    const savedShops = localStorage.getItem('electricityShops');
    const savedSettings = localStorage.getItem('electricitySettings');
    const savedHistory = localStorage.getItem('electricityHistory');
    if (savedShops) {
      setShops(JSON.parse(savedShops));
    } else {
      // Initialize with 10 default shops
      const defaultShops = Array.from({
        length: 10
      }, (_, i) => ({
        id: i + 1,
        name: `Shop ${i + 1}`,
        currentReading: 0,
        previousReading: 0,
        additionalCharges: 0
      }));
      setShops(defaultShops);
      localStorage.setItem('electricityShops', JSON.stringify(defaultShops));
    }
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);
  // Save shops whenever they change
  useEffect(() => {
    if (shops.length > 0) {
      localStorage.setItem('electricityShops', JSON.stringify(shops));
    }
  }, [shops]);
  // Save settings whenever they change
  useEffect(() => {
    localStorage.setItem('electricitySettings', JSON.stringify(settings));
  }, [settings]);
  // Save history whenever it changes
  useEffect(() => {
    localStorage.setItem('electricityHistory', JSON.stringify(history));
  }, [history]);
  // Add a new shop
  const addShop = () => {
    // Find the highest ID and add 1
    const maxId = shops.reduce((max, shop) => Math.max(max, shop.id), 0);
    const newShop: Shop = {
      id: maxId + 1,
      name: `Shop ${maxId + 1}`,
      currentReading: 0,
      previousReading: 0,
      additionalCharges: 0
    };
    setShops([...shops, newShop]);
  };
  // Delete a shop
  const deleteShop = (id: number) => {
    setShops(shops.filter(shop => shop.id !== id));
  };
  // Save current readings to history and reset for new month
  const saveReadingsToHistory = () => {
    const newHistoryEntry: HistoryEntry = {
      period: {
        ...selectedPeriod
      },
      shops: [...shops]
    };
    // Check if we already have an entry for this period
    const existingEntryIndex = history.findIndex(entry => entry.period.month === selectedPeriod.month && entry.period.year === selectedPeriod.year);
    let updatedHistory = [...history];
    if (existingEntryIndex >= 0) {
      // Replace the existing entry
      updatedHistory[existingEntryIndex] = newHistoryEntry;
    } else {
      // Add new entry
      updatedHistory.push(newHistoryEntry);
    }
    setHistory(updatedHistory);
    // Calculate next month
    let nextMonth = selectedPeriod.month + 1;
    let nextYear = selectedPeriod.year;
    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear += 1;
    }
    // Update previous readings with current ones and reset current readings
    const updatedShops = shops.map(shop => ({
      ...shop,
      previousReading: shop.currentReading,
      currentReading: 0
    }));
    setShops(updatedShops);
    setSelectedPeriod({
      month: nextMonth,
      year: nextYear
    });
  };
  // Get previous readings from history for a given period
  const getPreviousReadings = (period: ReadingPeriod) => {
    // Calculate previous month
    let prevMonth = period.month - 1;
    let prevYear = period.year;
    if (prevMonth < 0) {
      prevMonth = 11;
      prevYear -= 1;
    }
    const prevPeriod = {
      month: prevMonth,
      year: prevYear
    };
    // Find history entry for previous month
    const prevEntry = history.find(entry => entry.period.month === prevPeriod.month && entry.period.year === prevPeriod.year);
    if (prevEntry) {
      // Update shop previous readings from history
      const updatedShops = shops.map(shop => {
        const prevShop = prevEntry.shops.find(s => s.id === shop.id);
        if (prevShop) {
          return {
            ...shop,
            previousReading: prevShop.currentReading
          };
        }
        return shop;
      });
      setShops(updatedShops);
    }
  };
  return <div className="flex flex-col min-h-screen w-full bg-gray-50" data-id="element-0">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md" data-id="element-1">
        <h1 className="text-xl font-bold text-center" data-id="element-2">
          Shop Electricity Manager
        </h1>
      </header>
      {/* Main content */}
      <main className="flex-1 container mx-auto p-4 mb-16" data-id="element-3">
        {activeTab === 'readings' && <MeterReadingForm shops={shops} setShops={setShops} onSaveReadings={saveReadingsToHistory} selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} onPeriodChange={getPreviousReadings} data-id="element-4" />}
        {activeTab === 'invoices' && <InvoiceGenerator shops={shops} settings={settings} selectedPeriod={selectedPeriod} history={history} data-id="element-5" />}
        {activeTab === 'history' && <History history={history} settings={settings} data-id="element-6" />}
        {activeTab === 'settings' && <Settings settings={settings} setSettings={setSettings} shops={shops} setShops={setShops} onAddShop={addShop} onDeleteShop={deleteShop} data-id="element-7" />}
      </main>
      {/* Bottom navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 shadow-lg" data-id="element-8">
        <div className="flex justify-around items-center" data-id="element-9">
          <button onClick={() => setActiveTab('readings')} className={`flex flex-col items-center p-3 ${activeTab === 'readings' ? 'text-blue-600' : 'text-gray-600'}`} data-id="element-10">
            <HomeIcon size={20} data-id="element-11" />
            <span className="text-xs mt-1" data-id="element-12">Readings</span>
          </button>
          <button onClick={() => setActiveTab('invoices')} className={`flex flex-col items-center p-3 ${activeTab === 'invoices' ? 'text-blue-600' : 'text-gray-600'}`} data-id="element-13">
            <FileTextIcon size={20} data-id="element-14" />
            <span className="text-xs mt-1" data-id="element-15">Invoices</span>
          </button>
          <button onClick={() => setActiveTab('history')} className={`flex flex-col items-center p-3 ${activeTab === 'history' ? 'text-blue-600' : 'text-gray-600'}`} data-id="element-16">
            <HistoryIcon size={20} data-id="element-17" />
            <span className="text-xs mt-1" data-id="element-18">History</span>
          </button>
          <button onClick={() => setActiveTab('settings')} className={`flex flex-col items-center p-3 ${activeTab === 'settings' ? 'text-blue-600' : 'text-gray-600'}`} data-id="element-19">
            <SettingsIcon size={20} data-id="element-20" />
            <span className="text-xs mt-1" data-id="element-21">Settings</span>
          </button>
        </div>
      </nav>
    </div>;
}